'use client';

import { type SanityOperatingSchedule } from '@metmuseum/types';
import {
	getOperatingSchedule,
	type IHours,
	formatTime,
	getLocalMidnight,
} from 'lib/visit/locationSchedule';
import { useLocale, useTranslations } from 'next-intl';
import { formatDate } from 'helpers/date-time';
import styles from './location-hours-card.module.scss';
import MarkdownParser from 'components/rich-text/markdown-parser';
import Table from 'components/table';
import ClockSVG from 'assets/icons/clock.svg';

interface IProps {
	location: { schedules: SanityOperatingSchedule[] };
	showTodayMessage?: boolean;
}

const LocationHoursCard = ({ location, showTodayMessage = false }: IProps) => {
	const t = useTranslations();
	const locale = useLocale();

	const getHoursString = (hours: IHours, isClosed: boolean, short: boolean) => {
		const formattedHours = {
			openTime: hours.openTime
				? formatTime({
						date: getLocalMidnight(),
						time: hours.openTime,
						locale,
						short,
					})
				: '',
			closeTime: hours.closeTime
				? formatTime({
						date: getLocalMidnight(),
						time: hours.closeTime,
						locale,
						short: short,
					})
				: '',
		};
		if (isClosed) {
			return short ? t('closed') : t('museumClosed');
		} else {
			return short
				? t('openHoursShort', formattedHours)
				: t('openHours', formattedHours);
		}
	};

	const schedule = getOperatingSchedule(location);
	const { hoursThisWeek, scheduleDetails, isClosedToday, hours } = schedule;
	const formattedHours = {
		openTime: formatTime({
			date: getLocalMidnight(),
			time: hours.openTime,
			locale,
			short: false,
		}),
		closeTime: formatTime({
			date: getLocalMidnight(),
			time: hours.closeTime,
			locale,
			short: false,
		}),
	};
	const columns = [
		{
			key: 'day',
			label: t('day'),
		},
		{
			key: 'hours',
			label: t('hours'),
		},
	];
	const rows = hoursThisWeek.map((day) => {
		return {
			key: day.dateString,
			cells: {
				day: formatDate(day.date, { weekday: 'long' }, locale),
				hours: getHoursString(day.hours, day.isClosed, true),
			},
		};
	});

	return (
		<div>
			{/*
				Currently used only on PYV and Cloisters pages. Pages like Watson
				and 81st St Studio will need different static/localized text.
			*/}
			{showTodayMessage && (
				<div
					className={`${styles.currentHours} ${isClosedToday ? styles.closed : ''}`}
				>
					<ClockSVG />
					{isClosedToday ? t('museumClosed') : t('openHours', formattedHours)}
				</div>
			)}
			<Table columns={columns} rows={rows} />
			{!!scheduleDetails && (
				<div className={styles.scheduleDetails}>
					<MarkdownParser>{scheduleDetails}</MarkdownParser>
				</div>
			)}
		</div>
	);
};

export default LocationHoursCard;
